/**
 * Пара «фон сайдбара / текст / иконки / границы» из API и DEFAULT.
 * Нельзя оставлять тёмный текст на тёмном фоне или светлый на светлом — чиним по яркости фона.
 */

export function parseCssColorToRgb(input) {
  if (input == null || typeof input !== 'string') return null;
  const s = input.trim();
  if (!s) return null;
  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return null;
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const mRgb = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i)
    || s.match(/rgba?\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*[/)]/i);
  if (mRgb) {
    return { r: +mRgb[1], g: +mRgb[2], b: +mRgb[3] };
  }
  return null;
}

function relativeLuminance(rgb) {
  const lin = (v) => {
    const c = Math.max(0, Math.min(255, v)) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/** Фон сайдбара визуально светлый */
export function isLightBackgroundColor(cssColor) {
  const rgb = parseCssColorToRgb(cssColor);
  if (!rgb) return false;
  return relativeLuminance(rgb) > 0.55;
}

/** «Светлый» цвет текста/линии (подходит к тёмному фону) */
export function isLightForegroundColor(cssColor) {
  const rgb = parseCssColorToRgb(cssColor);
  if (!rgb) return false;
  return relativeLuminance(rgb) > 0.55;
}

/** «Тёмный» цвет текста (подходит к светлому фону) */
export function isDarkForegroundColor(cssColor) {
  const rgb = parseCssColorToRgb(cssColor);
  if (!rgb) return false;
  return relativeLuminance(rgb) < 0.45;
}

const LIGHT_RAIL = {
  text: 'rgba(0, 0, 0, 0.85)',
  icon: 'rgba(0, 0, 0, 0.55)',
  line: 'rgba(0, 0, 0, 0.08)',
};

const DARK_RAIL = {
  text: 'rgba(255, 255, 255, 0.85)',
  icon: 'rgba(255, 255, 255, 0.65)',
  line: 'rgba(255, 255, 255, 0.12)',
};

/**
 * @param {object} themeColors — результат getThemeColors() после merge API
 * @param {string} resolvedShellFallbackBg — getComputedStyle(... '--shell-sidebar-fallback-bg')
 */
export function resolveSidebarThemeCssVars(themeColors, resolvedShellFallbackBg) {
  const shell = (resolvedShellFallbackBg && String(resolvedShellFallbackBg).trim()) || '';
  const bg =
    (themeColors.sidebarBackground && String(themeColors.sidebarBackground).trim()) ||
    shell ||
    '#001529';

  const bgIsLight = isLightBackgroundColor(bg);

  let text = themeColors.sidebarText;
  let icon = themeColors.sidebarIcon;
  let border = themeColors.sidebarBorder;
  let divider = themeColors.sidebarDivider;

  if (bgIsLight) {
    // Светлый rail — нужен тёмный текст; если пришли «белые» токены с тёмного rail — заменить
    if (!text || isLightForegroundColor(text)) {
      text = LIGHT_RAIL.text;
    }
    if (!icon || isLightForegroundColor(icon)) {
      icon = LIGHT_RAIL.icon;
    }
    if (!border || isLightForegroundColor(border)) {
      border = LIGHT_RAIL.line;
    }
    if (!divider || isLightForegroundColor(divider)) {
      divider = LIGHT_RAIL.line;
    }
  } else {
    // Тёмный rail (#001529 и т.д.) — нужен светлый текст; если пришли тёмные токены со светлого rail — заменить
    if (!text || isDarkForegroundColor(text)) {
      text = DARK_RAIL.text;
    }
    if (!icon || isDarkForegroundColor(icon)) {
      icon = DARK_RAIL.icon;
    }
    if (!border || isDarkForegroundColor(border)) {
      border = DARK_RAIL.line;
    }
    if (!divider || isDarkForegroundColor(divider)) {
      divider = DARK_RAIL.line;
    }
  }

  return {
    '--theme-sidebar-background': bg,
    '--theme-sidebar-selected': themeColors.sidebarSelected || 'var(--brand)',
    '--theme-sidebar-text': text || 'var(--shell-sidebar-rail-text)',
    '--theme-sidebar-icon': icon || 'var(--shell-sidebar-rail-icon)',
    '--theme-sidebar-border': border || 'var(--shell-sidebar-fallback-border)',
    '--theme-sidebar-divider': divider || 'var(--shell-sidebar-rail-divider)',
  };
}
